<template>
  <div class="edit-contacts-share-page">
    <v-container class="py-10" max-width="960">
      <!-- Header -->
      <div class="page-header mb-2">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="memorial?.nameOfPerson ? '-' + memorial.nameOfPerson : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>

      <!-- Memorial Link Section -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-link</v-icon>
          Memory Link
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-4">
            Share this link with family and friends so they can view and contribute to the memory.
          </p>

          <div class="link-row mb-4">
            <v-text-field
              :model-value="memorialLink"
              readonly
              variant="outlined"
              density="comfortable"
              hide-details
              class="link-field"
            ></v-text-field>
            <v-btn
              color="primary"
              variant="outlined"
              class="copy-btn"
              @click="copyLink"
            >
              <v-icon start>{{ linkCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ linkCopied ? 'Copied!' : 'Copy' }}
            </v-btn>
          </div>

          <div class="social-buttons mb-6">
            <v-btn
              icon
              size="large"
              color="#1877f2"
              @click="shareOnFacebook"
              class="mr-2"
            >
              <v-icon>mdi-facebook</v-icon>
            </v-btn>
            <v-btn
              icon
              size="large"
              color="#1da1f2"
              @click="shareOnTwitter"
              class="mr-2"
            >
              <v-icon>mdi-twitter</v-icon>
            </v-btn>
            <v-btn
              icon
              size="large"
              color="#25d366"
              @click="shareOnWhatsApp"
              class="mr-2"
            >
              <v-icon>mdi-whatsapp</v-icon>
            </v-btn>
            <v-btn
              icon
              size="large"
              color="primary"
              @click="shareByEmail"
            >
              <v-icon>mdi-email</v-icon>
            </v-btn>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="d-flex align-start ga-3">
            <v-checkbox
              v-model="autoApproveContributions"
              color="primary"
              hide-details
              density="comfortable"
              class="auto-approve-checkbox"
              :disabled="savingAutoApprove"
              @update:model-value="saveAutoApproveContributions"
            >
              <template v-slot:label>
                <div>
                  <span class="text-body-1">Automatically approve contributions</span>
                  <div class="text-caption text-grey">When enabled, contributions from visitors are published immediately without your review.</div>
                </div>
              </template>
            </v-checkbox>
            <v-progress-circular
              v-if="savingAutoApprove"
              indeterminate
              size="20"
              width="2"
              color="primary"
            ></v-progress-circular>
          </div>
        </v-card-text>
      </v-card>

      <!-- Contacts Section -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-account-multiple</v-icon>
          Contacts
        </v-card-title>
        <v-card-text class="pa-6">
          <v-alert v-if="contacts.length === 0" type="info" variant="tonal" class="mb-4">
            No contacts added yet. You can add contacts to invite them to the memorial.
          </v-alert>

          <div class="d-flex justify-end mb-3">
            <v-btn
              color="primary"
              variant="outlined"
              size="small"
              prepend-icon="mdi-email-search-outline"
              @click="openInvitePreview"
            >
              Preview Invite
            </v-btn>
          </div>

          <!-- Add Contact Form -->
          <div class="add-contact-form mb-4">
            <v-text-field
              v-model="newContactEmail"
              label="Add email address"
              type="email"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :error-messages="newContactError"
              @keyup.enter="addContact"
            >
              <template v-slot:append-inner>
                <v-btn
                  color="primary"
                  variant="text"
                  size="small"
                  class="no-outline-button"
                  @click="addContact"
                  :disabled="!newContactEmail || addingContact"
                  :loading="addingContact"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add
                </v-btn>
              </template>
            </v-text-field>
          </div>

          <v-list v-if="contacts.length > 0">
            <v-list-item
              v-for="(contact, index) in contacts"
              :key="contact.id || index"
              class="contact-item mb-3"
            >
              <template v-slot:prepend>
                <!-- RSVP Status Icon -->
                <v-icon
                  v-if="contact.rsvp?.status === 'ATTENDING'"
                  color="success"
                  size="24"
                  class="mr-3"
                >
                  mdi-check-circle
                </v-icon>
                <v-icon
                  v-else-if="contact.rsvp?.status === 'NOT_ATTENDING'"
                  color="error"
                  size="24"
                  class="mr-3"
                >
                  mdi-close-circle
                </v-icon>
                <v-icon
                  v-else-if="contact.rsvp?.status === 'UNSURE'"
                  color="warning"
                  size="24"
                  class="mr-3"
                >
                  mdi-help-circle
                </v-icon>
                <v-icon
                  v-else
                  color="grey"
                  size="24"
                  class="mr-3"
                >
                  mdi-email-outline
                </v-icon>
              </template>

              <div class="contact-details">
                <div v-if="!contact.editing">
                  <v-list-item-title>{{ contact.email || contact.phone }}</v-list-item-title>
                  <v-list-item-subtitle v-if="contact.rsvp?.status">
                    {{ getRsvpStatusText(contact.rsvp.status) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-else-if="!contact.dateInviteSent" class="text-grey">
                    No invite sent
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-else class="text-grey">
                    Invite sent {{ formatDate(contact.dateInviteSent) }}, no response yet
                  </v-list-item-subtitle>
                </div>

                <div v-else class="edit-form">
                  <v-text-field
                    v-model="contact.editedEmail"
                    label="Email address"
                    type="email"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="mb-2"
                  ></v-text-field>
                </div>
              </div>

              <template v-slot:append>
                <div class="contact-actions d-flex align-center ga-2">
                  <template v-if="!contact.editing">
                    <!-- Edit button -->
                    <v-btn
                      v-if="contact.email"
                      color="primary"
                      variant="text"
                      size="small"
                      @click="startEditContact(contact)"
                    >
                      Edit
                    </v-btn>

                    <!-- Send/Resend Invite button (only for non-responders with email, hide if just sent) -->
                    <v-btn
                      v-if="!contact.rsvp && contact.email && contact.sendStatus !== 'success'"
                      color="primary"
                      variant="outlined"
                      size="small"
                      @click="resendInvite(contact)"
                      :disabled="contact.sending"
                    >
                      {{ contact.dateInviteSent ? 'Resend Invite' : 'Send Invite' }}
                    </v-btn>

                    <v-progress-circular
                      v-if="contact.sending"
                      indeterminate
                      size="20"
                      width="2"
                      color="primary"
                    ></v-progress-circular>

                    <!-- Success message - keep visible -->
                    <div v-if="contact.sendStatus === 'success'" class="d-flex align-center">
                      <v-icon color="success" size="24" class="mr-2">
                        mdi-check-circle
                      </v-icon>
                      <span class="text-success text-body-2">Invite sent successfully</span>
                    </div>

                    <v-icon
                      v-if="contact.sendStatus === 'error'"
                      color="error"
                      size="24"
                    >
                      mdi-close-circle
                    </v-icon>
                  </template>

                  <template v-else>
                    <!-- Save button -->
                    <v-btn
                      color="primary"
                      variant="elevated"
                      size="small"
                      @click="saveContact(contact)"
                      :loading="contact.saving"
                    >
                      Save
                    </v-btn>

                    <!-- Cancel button -->
                    <v-btn
                      color="grey"
                      variant="text"
                      size="small"
                      @click="cancelEditContact(contact)"
                    >
                      Cancel
                    </v-btn>
                  </template>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>

      <!-- QR Code Section -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-qrcode</v-icon>
          QR Code
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-4">
            Generate a QR code that guests can scan to view and contribute to the memory. Scanning opens the contribution form directly.
          </p>

          <!-- No QR code yet -->
          <template v-if="!qrCode">
            <v-textarea
              v-model="qrMessage"
              label="Optional message below the QR code (on printed page)"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              counter="500"
              hide-details="auto"
              class="mb-4"
            ></v-textarea>
            <v-btn
              color="primary"
              variant="outlined"
              :loading="qrGenerating"
              prepend-icon="mdi-qrcode-plus"
              @click="generateQrCode"
            >
              Generate QR Code
            </v-btn>
          </template>

          <!-- QR code exists -->
          <template v-else>
            <div class="qr-display mb-4">
              <img
                v-if="qrCode.qrImageUrl"
                :src="qrCode.qrImageUrl"
                alt="QR Code"
                class="qr-image"
              />
            </div>

            <!-- Message editor -->
            <v-textarea
              v-model="qrMessage"
              label="Message below QR code (on printed page)"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              counter="500"
              hide-details="auto"
              class="mb-2"
              @blur="saveQrMessage"
            ></v-textarea>
            <p class="text-caption text-grey mb-4">Message is saved automatically when you click away.</p>

            <div class="d-flex flex-wrap ga-2 mb-2">
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-printer"
                @click="printQrCode"
              >
                Print QR
              </v-btn>
              <v-btn
                color="error"
                variant="outlined"
                prepend-icon="mdi-qrcode-remove"
                :loading="qrDisabling"
                @click="confirmDisableQr = true"
              >
                Disable QR Access
              </v-btn>
            </div>
            <p class="text-caption text-grey">
              Disabling removes the QR image and blocks any further QR-code contributions. The link in existing printed materials will stop working.
            </p>
          </template>
        </v-card-text>
      </v-card>

      <!-- Dashboard Section -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-view-dashboard</v-icon>
          Dashboard
        </v-card-title>
        <v-card-text class="pa-6 text-center">
          <p class="text-body-1 mb-4">
            To go back to the dashboard and manage your memory, click on the button below.
          </p>
          <v-btn
            variant="text"
            size="large"
            @click="goToDashboard"
          >
            Back to Dashboard
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Invite Preview Dialog -->
    <v-dialog v-model="showInvitePreview" max-width="700" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between pa-4">
          <span>Email Invite Preview</span>
          <v-btn icon variant="text" @click="showInvitePreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="height: 600px;">
          <div v-if="invitePreviewLoading" class="d-flex align-center justify-center" style="height: 100%;">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="invitePreviewError" class="d-flex align-center justify-center pa-6" style="height: 100%;">
            <v-alert type="error" variant="tonal">{{ invitePreviewError }}</v-alert>
          </div>
          <iframe
            v-else
            :srcdoc="invitePreviewHtml"
            class="invite-preview-frame"
            sandbox="allow-same-origin"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- QR Disable Confirm Dialog -->
    <v-dialog v-model="confirmDisableQr" max-width="480" persistent>
      <v-card>
        <v-card-title>
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Disable QR Code Access?
        </v-card-title>
        <v-card-text>
          This will permanently delete the QR code image and block all future contributions via the QR link.
          Any existing printed materials using this QR code will stop working.
          This cannot be undone — you would need to generate a new QR code (with a different link).
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="confirmDisableQr = false" :disabled="qrDisabling">Cancel</v-btn>
          <v-btn color="error" :loading="qrDisabling" @click="disableQrCode">Yes, Disable</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text>
          <p>{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import ProfileMenu from '../components/ProfileMenu.vue'
import { API_BASE_URL } from '@/config/branding'

const router = useRouter()
const store = useMemorialStore()

const loading = ref(true)
const contacts = ref([])
const memorial = ref(null)
const linkCopied = ref(false)
const newContactEmail = ref('')
const newContactError = ref('')
const addingContact = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const showInvitePreview = ref(false)
const invitePreviewHtml = ref('')
const invitePreviewLoading = ref(false)
const invitePreviewError = ref('')
const autoApproveContributions = ref(false)
const savingAutoApprove = ref(false)

const qrCode = ref(null)
const qrMessage = ref('')
const qrGenerating = ref(false)
const qrDisabling = ref(false)
const confirmDisableQr = ref(false)

const memorialLink = computed(() => {
  if (memorial.value?.shareSlug) {
    const baseUrl = window.location.origin
    return `${baseUrl}/for/${memorial.value.shareSlug}`
  }
  return ''
})

const loadData = async () => {
  loading.value = true
  try {
    const response = await apiService.getMemorial(store.memorialUuid)
    if (response.success && response.memorial) {
      memorial.value = response.memorial
      autoApproveContributions.value = !!response.memorial.autoApproveContributions
      // Load existing QR code for this memorial
      try {
        const qrResponse = await apiService.getQrCode(store.memorialUuid, store.apiToken)
        if (qrResponse.success) {
          qrCode.value = qrResponse.qrCode
          qrMessage.value = qrResponse.qrCode?.message || ''
        }
      } catch (e) {
        console.error('Error loading QR code:', e)
      }

      // Load contacts and match with RSVPs
      const allRsvps = response.memorial.rsvps || []
      const allContacts = response.memorial.contacts || []

      // Create a map of RSVPs by email/phone
      const rsvpMap = new Map()
      allRsvps.forEach(rsvp => {
        const key = rsvp.email || rsvp.phone
        if (key) {
          rsvpMap.set(key.toLowerCase(), rsvp)
        }
      })

      // Match contacts with their RSVPs
      contacts.value = allContacts.map(contact => {
        const contactKey = (contact.email || contact.phone || '').toLowerCase()
        const matchedRsvp = rsvpMap.get(contactKey)

        return {
          ...contact,
          rsvp: matchedRsvp || null,
          editing: false,
          editedEmail: contact.email || '',
          sending: false,
          sendStatus: null,
          saving: false
        }
      })
    }
  } catch (error) {
    console.error('Error loading memorial data:', error)
  } finally {
    loading.value = false
  }
}

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(memorialLink.value)
    linkCopied.value = true
    setTimeout(() => {
      linkCopied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}

const shareOnFacebook = () => {
  const url = encodeURIComponent(memorialLink.value)
  const text = encodeURIComponent(`Please join me in celebrating the life of ${memorial.value?.nameOfPerson || 'a loved one'}. View their memorial and share your own memories.`)
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank')
}

const shareOnTwitter = () => {
  const url = encodeURIComponent(memorialLink.value)
  const text = encodeURIComponent(`Celebrating the life of ${memorial.value?.nameOfPerson || 'a loved one'}. Share your memories: ${url}`)
  window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank')
}

const shareByEmail = () => {
  const subject = encodeURIComponent(`Memorial for ${memorial.value?.nameOfPerson || 'a loved one'}`)
  const body = encodeURIComponent(`Please join me in celebrating the life of ${memorial.value?.nameOfPerson || 'a loved one'}.\n\nView their memorial and share your own memories:\n${memorialLink.value}`)
  window.location.href = `mailto:?subject=${subject}&body=${body}`
}

const shareOnWhatsApp = () => {
  const text = encodeURIComponent(`Please join me in celebrating the life of ${memorial.value?.nameOfPerson || 'a loved one'}. View their memorial and share your own memories: ${memorialLink.value}`)
  window.open(`https://wa.me/?text=${text}`, '_blank')
}

const getRsvpStatusText = (status) => {
  const statusMap = {
    'ATTENDING': 'Attending',
    'NOT_ATTENDING': 'Not Attending',
    'UNSURE': 'Not Sure'
  }
  return statusMap[status] || status
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const addContact = async () => {
  if (!newContactEmail.value || addingContact.value) return

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(newContactEmail.value)) {
    newContactError.value = 'Please enter a valid email address'
    return
  }

  // Check for duplicates
  if (contacts.value.some(c => c.email === newContactEmail.value)) {
    newContactError.value = 'This contact already exists'
    return
  }

  addingContact.value = true
  newContactError.value = ''

  try {
    const response = await apiService.addContact(
      store.memorialUuid,
      { email: newContactEmail.value },
      store.apiToken
    )

    if (response.success && response.contact) {
      // Add the new contact to the list
      contacts.value.push({
        uuid: response.contact.uuid,
        id: response.contact.id,
        email: response.contact.email,
        phone: response.contact.phone,
        rsvp: response.contact.rsvp,
        dateInviteSent: response.contact.dateInviteSent,
        editing: false,
        sending: false,
        sendStatus: null
      })
      newContactEmail.value = ''
    } else {
      newContactError.value = response.msg || 'Failed to add contact'
    }
  } catch (error) {
    console.error('Error adding contact:', error)
    newContactError.value = 'An error occurred while adding the contact'
  } finally {
    addingContact.value = false
  }
}

const startEditContact = (contact) => {
  contact.editing = true
  contact.editedEmail = contact.email || ''
}

const cancelEditContact = (contact) => {
  contact.editing = false
  contact.editedEmail = contact.email || ''
}

const saveContact = async (contact) => {
  contact.saving = true
  try {
    const response = await apiService.updateContact(
      store.memorialUuid,
      contact.uuid,
      { email: contact.editedEmail },
      store.apiToken
    )

    if (response.success) {
      contact.email = contact.editedEmail
      contact.editing = false
    } else {
      console.error('Failed to update contact:', response.msg)
      errorMessage.value = 'Failed to update contact. Please try again.'
      showErrorDialog.value = true
    }
  } catch (error) {
    console.error('Error updating contact:', error)
    errorMessage.value = 'Error updating contact. Please try again.'
    showErrorDialog.value = true
  } finally {
    contact.saving = false
  }
}

const resendInvite = async (contact) => {
  if (!contact.uuid) {
    console.error('Contact UUID missing')
    return
  }

  contact.sending = true
  contact.sendStatus = null

  try {
    const response = await apiService.post('/memory/resend-invite', {
      contactUuid: contact.uuid,
      memorialUuid: store.memorialUuid
    })

    if (response.success) {
      console.log('Invite sent successfully to', contact.email)
      contact.sendStatus = 'success'
      // Update dateInviteSent to current date so it shows "Invite sent [date]"
      contact.dateInviteSent = Date.now()

      // Clear success status after 2 seconds
      setTimeout(() => {
        contact.sendStatus = null
      }, 2000)
    } else {
      console.error('Failed to send invite:', response.msg)
      contact.sendStatus = 'error'

      // Clear error status after 2 seconds
      setTimeout(() => {
        contact.sendStatus = null
      }, 2000)
    }
  } catch (error) {
    console.error('Error sending invite:', error)
    contact.sendStatus = 'error'

    // Clear error status after 2 seconds
    setTimeout(() => {
      contact.sendStatus = null
    }, 2000)
  } finally {
    contact.sending = false
  }
}

const openInvitePreview = async () => {
  showInvitePreview.value = true
  invitePreviewLoading.value = true
  invitePreviewError.value = ''
  invitePreviewHtml.value = ''
  try {
    const baseUrl = process.env.NODE_ENV === 'production'
      ? API_BASE_URL
      : 'http://localhost:8088'
    const response = await fetch(
      `${baseUrl}/memory/invite-preview/${store.memorialUuid}`,
      { headers: { pid: store.apiToken } }
    )
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    invitePreviewHtml.value = await response.text()
  } catch (error) {
    console.error('Error loading invite preview:', error)
    invitePreviewError.value = 'Failed to load invite preview.'
  } finally {
    invitePreviewLoading.value = false
  }
}

const generateQrCode = async () => {
  qrGenerating.value = true
  try {
    const response = await apiService.generateQrCode(store.memorialUuid, qrMessage.value, store.apiToken)
    if (response.success) {
      qrCode.value = response.qrCode
      qrMessage.value = response.qrCode?.message || ''
    } else {
      errorMessage.value = response.msg || 'Failed to generate QR code.'
      showErrorDialog.value = true
    }
  } catch (e) {
    console.error('Error generating QR code:', e)
    errorMessage.value = 'An error occurred generating the QR code.'
    showErrorDialog.value = true
  } finally {
    qrGenerating.value = false
  }
}

const saveQrMessage = async () => {
  if (!qrCode.value) return
  try {
    await apiService.updateQrMessage(store.memorialUuid, qrMessage.value, store.apiToken)
    if (qrCode.value) qrCode.value.message = qrMessage.value
  } catch (e) {
    console.error('Error saving QR message:', e)
  }
}

const disableQrCode = async () => {
  qrDisabling.value = true
  try {
    const response = await apiService.disableQrCode(store.memorialUuid, store.apiToken)
    if (response.success) {
      qrCode.value = null
      qrMessage.value = ''
      confirmDisableQr.value = false
    } else {
      errorMessage.value = response.msg || 'Failed to disable QR code.'
      showErrorDialog.value = true
      confirmDisableQr.value = false
    }
  } catch (e) {
    console.error('Error disabling QR code:', e)
    errorMessage.value = 'An error occurred disabling the QR code.'
    showErrorDialog.value = true
    confirmDisableQr.value = false
  } finally {
    qrDisabling.value = false
  }
}

const printQrCode = () => {
  const memorialName = memorial.value?.nameOfPerson || 'Memory'
  const qrImageUrl = qrCode.value?.qrImageUrl || ''
  const message = qrMessage.value || ''
  const memorialUrl = memorialLink.value

  const printWindow = window.open('', '_blank')
  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>QR Code — ${memorialName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Georgia, serif;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 40px 60px;
      color: #1a1a1a;
    }
    h1 { font-size: 28px; font-weight: normal; margin-bottom: 12px; text-align: center; }
    .subtitle {
      font-size: 15px;
      color: #555;
      text-align: center;
      max-width: 420px;
      margin-bottom: 32px;
      line-height: 1.6;
    }
    .qr-img { width: 260px; height: 260px; display: block; margin: 0 auto 24px; }
    .url { font-size: 11px; color: #999; text-align: center; margin-bottom: 24px; word-break: break-all; }
    .message {
      font-size: 16px;
      color: #333;
      text-align: center;
      max-width: 420px;
      margin-top: 8px;
      line-height: 1.7;
      font-style: italic;
    }
  </style>
</head>
<body>
  <h1>In Memory of ${memorialName}</h1>
  <p class="subtitle">
    Scan this QR code to view the memory and share your own memories, stories and photos.
  </p>
  <img class="qr-img" src="${qrImageUrl}" alt="QR Code" />
  <p class="url">${memorialUrl}</p>
  ${message ? `<p class="message">${message}</p>` : ''}
  <script>window.onload = function() { window.print(); }<${'/' + 'script'}>
</body>
</html>`)
  printWindow.document.close()
}

const saveAutoApproveContributions = async (value) => {
  savingAutoApprove.value = true
  try {
    const response = await apiService.post(`/memory/update/${store.memorialUuid}`, {
      autoApproveContributions: value
    })
    if (response.success) {
      if (memorial.value) {
        memorial.value.autoApproveContributions = value
      }
    } else {
      autoApproveContributions.value = !value
    }
  } catch (error) {
    console.error('Error saving auto-approve setting:', error)
    autoApproveContributions.value = !value
  } finally {
    savingAutoApprove.value = false
  }
}

const goToDashboard = () => {
  router.push('/memory/dashboard')
}


onMounted(() => {
  if (!store.apiToken || !store.memorialUuid) {
    router.push('/login')
    return
  }
  loadData()
})
</script>

<style scoped>
.edit-contacts-share-page {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.auto-approve-checkbox :deep(.v-selection-control) {
  min-height: auto;
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.section-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 24px 45px -20px rgba(15, 23, 42, 0.2);
  background: var(--v-theme-surface);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}

.primary-header {
  background: white !important;
  color: rgb(var(--v-theme-primary)) !important;
  border-bottom: 1px solid var(--v-theme-border);
}

.link-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  max-width: 600px;
}

.link-field {
  flex: 1;
  margin-top: 0 !important;
}

.copy-btn {
  align-self: stretch;
  height: auto !important;
  min-height: 0 !important;
}

.social-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.contact-item {
  border: 1px solid var(--v-theme-border);
  border-radius: 12px;
  padding: 12px;
  background: var(--v-theme-background);
}

.contact-details {
  flex: 1;
}

.edit-form {
  max-width: 400px;
}

.contact-actions {
  gap: 8px;
}

.qr-display {
  display: flex;
  justify-content: flex-start;
}

.qr-image {
  width: 180px;
  height: 180px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  padding: 8px;
  background: white;
}

.invite-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

@media (max-width: 600px) {
  .profile-menu-wrapper {
    right: -10px;
  }

  .contact-actions {
    flex-direction: column;
    align-items: flex-end;
  }
}
</style>
